'use client';

/**
 * AppShell — responsive pivot.
 *
 * Branches between `DesktopShell` and `MobileShell` based on viewport width
 * (keyed to `MOBILE_BREAKPOINT` in `useIsMobile`). Desktop is the SSR
 * default so server-rendered HTML ships the full three-pane markup.
 *
 * SSR strategy (picked explicitly — see `use-is-mobile.ts` for rejected
 * alternatives): we gate the whole shell tree behind `hydrated`. Before
 * the first client effect runs, the shell is rendered invisible
 * (`opacity-0`); on hydration it fades to `opacity-100` with the correct
 * shell chosen. That costs one frame of blank screen but prevents mobile
 * users from seeing a flash of desktop markup (and vice-versa).
 *
 * Dev override: append `?forceMobile=1` to the URL to render the mobile
 * shell on any viewport — useful for testing without resizing.
 *
 * @component_role: Responsive Shell Switch
 * @component_type: Layout Container
 * @domain: Frontend Shell
 * @layer: presentation
 */

import { useIsMobile } from '@/lib/hooks/use-is-mobile';
import { DesktopShell } from './desktop-shell';
import { MobileShell } from './mobile-shell';

export function AppShell() {
  const { isMobile, hydrated } = useIsMobile();
  const Shell = isMobile ? MobileShell : DesktopShell;

  return (
    <div
      className={`transition-opacity duration-75 ${hydrated ? 'opacity-100' : 'opacity-0'}`}
      aria-busy={!hydrated}
    >
      <Shell />
    </div>
  );
}
